/**
 * v67 (band designs W2–W5) — the host-side rules behind the Gems tab's
 * "Pattern" style: the tile, the layer cards, the library, Surprise me,
 * Start from current row and Convert to free stones.
 *
 * Pure (no React, no DOM), so `scripts/check-ring-patterns.ts` can pin every
 * rule without a browser. The components in `./patterns/` render from these
 * tables and send what these functions build; nothing else decides.
 *
 * A pattern travels as `applyRingDesign({ diamond: { style: "pattern" },
 * stonePattern })` — there is no dedicated setter (contract v67 added no method
 * name for it). `stonePattern.layers` is always sent WHOLE: the design patch
 * merges nested blocks, but a list is replaced, so each write carries the full
 * clamped pattern.
 */
import {
  STONE_PATTERN_LIBRARY,
  STONE_PATTERN_LIBRARY_BAND,
  STONE_PATTERN_MAX_LAYERS,
  STONE_PATTERN_MIN_GAP_MM,
  STONE_PATTERN_SETTINGS,
  clampFreeStones,
  clampStonePattern,
  clampStonePatternLayer,
  expandStonePattern,
  resolveStonePatternLayer,
  stonePatternFromLibrary,
  surpriseStonePattern,
} from "../../shared/api-contract";
import type {
  DiamondSetting,
  DiamondStyle,
  FreeStone,
  PatternRail,
  PatternStone,
  RingBuilderClientApi,
  RingDesignPatch,
  RingState,
  ResolvedStonePatternLayer,
  StonePattern,
  StonePatternBand,
  StonePatternLayer,
  StonePatternLibraryEntry,
  StonePatternMirror,
  StonePatternPath,
  StonePatternPocketShape,
  StonePatternResult,
  StonePatternSetting,
  StonePatternSizingMode,
  StoneSpacing,
} from "../../shared/api-contract";
import type { Caps } from "../editor/caps";

export const PATTERN_STYLE: DiamondStyle = "pattern";

// ─── the tile ────────────────────────────────────────────────────────────────

/** The Pattern tile exists only on a viewer that reads `stonePattern` (v67).
 *  An older viewer would store the style id and draw nothing. */
export const showPatternTile = (caps: Pick<Caps, "ringDesignV67">): boolean => caps.ringDesignV67;

/** The served style catalogue minus "pattern": the Gems tab draws its own tile
 *  for it (gated above), so a viewer that also lists it never shows two. */
export function withoutPatternStyle<T extends { id: string }>(styles: readonly T[]): T[] {
  return styles.filter((s) => s.id !== PATTERN_STYLE);
}

// ─── the band the generator sees ─────────────────────────────────────────────

/**
 * The band, as the generator reads it. `circumferenceMM` is 2π × the outer
 * crest radius (the path-groove plane's period): the bore radius plus the
 * wall. The engine expands with its exact crest radius; on a crowned section
 * the two differ by hundredths of a mm, which moves a full eternity's last
 * gap, never a stone off the band.
 */
export function patternBand(state: Pick<RingState, "widthMM" | "thicknessMM" | "spec" | "diamond">): Required<Omit<StonePatternBand, "edgeMarginMM">> {
  const boreMM = state.spec?.innerDiameterMM && state.spec.innerDiameterMM > 0 ? state.spec.innerDiameterMM : 17.3;
  return {
    widthMM: state.widthMM,
    circumferenceMM: Math.PI * (boreMM + 2 * state.thicknessMM),
    stoneSizeMM: state.diamond.stoneSizeMM,
  };
}

/** The outer crest radius the band above implies, mm. */
export const bandRadiusMM = (band: Pick<StonePatternBand, "circumferenceMM">) => (band.circumferenceMM ?? 65) / (2 * Math.PI);

// ─── what every write sends ──────────────────────────────────────────────────

/** The one patch a pattern edit sends: the style plus the WHOLE clamped pattern. */
export function patternPatch(pattern: StonePattern): RingDesignPatch {
  const clamped = clampStonePattern(pattern) ?? { layers: [defaultLayer()] };
  return { diamond: { style: PATTERN_STYLE }, stonePattern: clamped };
}

/** Send a pattern through the design call. Returns the promise the caller
 *  hands to `apply` (chips) or drops (slider drags, which the kit batches). */
export function sendPattern(r: Pick<RingBuilderClientApi, "applyRingDesign">, pattern: StonePattern) {
  return r.applyRingDesign(patternPatch(pattern));
}

/** A first layer when there is nothing to start from: a half eternity at the
 *  row's stone size, centred at the top. */
export function defaultLayer(): StonePatternLayer {
  return { path: "row", spanTurns: 0.5, spacing: "gap", gapMM: 0.2 };
}

/** The live pattern, or one layer seeded from the run when the ring has none. */
export function currentPattern(state: RingState): StonePattern {
  return clampStonePattern(state.stonePattern) ?? { layers: [layerFromRun(state)] };
}

/** The patch the Pattern TILE sends: keep a pattern the ring already carries
 *  (switching away and back must not lose it), else start from the row. */
export function patternTilePatch(state: RingState): RingDesignPatch {
  return patternPatch(currentPattern(state));
}

export function replaceLayer(p: StonePattern, i: number, layer: StonePatternLayer): StonePattern {
  const layers = p.layers.slice();
  layers[i] = clampStonePatternLayer(layer);
  return { ...p, layers };
}

export const canAddLayer = (p: StonePattern) => p.layers.length < STONE_PATTERN_MAX_LAYERS;
export const canRemoveLayer = (p: StonePattern) => p.layers.length > 1;

/** A new layer goes on the opposite edge of the band from the first, so it is
 *  visible at once rather than drawn on top of layer 1. */
export function addLayer(p: StonePattern, band: Pick<StonePatternBand, "widthMM">): StonePattern {
  if (!canAddLayer(p)) return p;
  const first = resolveStonePatternLayer(p.layers[0]);
  const side = first.centerYMM > 0 ? -1 : 1;
  const layer: StonePatternLayer = { path: "row", count: 5, sizeMM: 1, centerYMM: round2(side * band.widthMM * 0.25) };
  return { ...p, layers: [...p.layers, layer] };
}

export function removeLayer(p: StonePattern, i: number): StonePattern {
  if (!canRemoveLayer(p)) return p;
  return { ...p, layers: p.layers.filter((_, k) => k !== i) };
}

// ─── Start from current row ──────────────────────────────────────────────────

/** Pitch of each stone-multiple spacing, as the engine lays a run out
 *  (`STONE_SPACINGS` in the viewer's BandConfig): girdle gap = pitch − d. */
const SPACING_PITCH: Partial<Record<StoneSpacing, { factor: number; minWebMM?: number }>> = {
  close: { factor: 1.03, minWebMM: 0.062 },
  touching: { factor: 1.18 },
  "half-stone": { factor: 1.55 },
  "full-stone": { factor: 2.05 },
  double: { factor: 3.05 },
};
const DEFAULT_PITCH_FACTOR = 1.18;
const RING_FRACTION: Partial<Record<StoneSpacing, number>> = { "half-ring": 0.5, "third-ring": 1 / 3, "full-ring": 1 };
/** The reference's Left/Right inset (viewer `POSITION_INSET_MM`). */
const POSITION_INSET_MM = 0.42;
/** The seat's half-width as a share of the stone (viewer `SEAT_HALF_FACTOR`). */
const SEAT_HALF_FACTOR = 0.62;
const EDGE_MARGIN_MM = 0.15;

/** The girdle-to-girdle gap a run's spacing leaves, mm (never under the
 *  generator's own floor). */
export function runGapMM(spacing: StoneSpacing | undefined, dia: number): number {
  const s = spacing ? SPACING_PITCH[spacing] : undefined;
  const pitch = s ? (s.minWebMM !== undefined ? Math.max(dia * s.factor, dia + s.minWebMM) : dia * s.factor) : dia * DEFAULT_PITCH_FACTOR;
  return Math.max(STONE_PATTERN_MIN_GAP_MM, round3(pitch - dia));
}

/** The engine's `rowOffsetMM`: where `rowPosition = ±1` puts a run. */
export function rowOffsetMM(halfWidth: number, dia: number): number {
  const avail = Math.max(0, halfWidth - dia / 2);
  return avail - Math.min(POSITION_INSET_MM, avail);
}

/** How a run's stones are held, in the pattern's vocabulary. */
export function patternSettingFromRun(state: Pick<RingState, "diamond" | "channel">): StonePatternSetting {
  const d = state.diamond;
  switch (d.setting) {
    case "flush": case "only": case "pave": case "shared-prong": case "prongs": return d.setting;
    default: break;
  }
  if (/bezel/.test(d.style)) return "bezel";
  if (typeof d.stoneShape === "string" && d.stoneShape.startsWith("set:")) return "prongs";
  return "flush";
}

/**
 * Layer 1 seeded from the run's own law (`gemPlacement.mainRun`): the same
 * count, stone size (left to follow the Stone Size slider), gap, place round
 * the ring and place across it. A ring-fraction spacing or a full spread is a
 * share of the ring; a fixed count is just the stones; a cross row runs across
 * the band; a channel row keeps its trench as a channel recess.
 */
export function layerFromRun(state: RingState): StonePatternLayer {
  const d = state.diamond;
  const band = patternBand(state);
  const C = band.circumferenceMM;
  const dia = d.stoneSizeMM;
  // Under the Pattern style the run's own fields are still on the ring (they
  // are only hidden), so "the current row" is that run read as a plain row.
  if (d.style === "none" || d.style === "free-place") {
    return clampStonePatternLayer(defaultLayer());
  }
  const halfW = state.widthMM / 2;
  const layer: StonePatternLayer = { path: "row", gapMM: runGapMM(d.spacing, dia) };
  const setting = patternSettingFromRun(state);
  if (setting !== "flush") layer.setting = setting;
  if (state.channel?.enabled && !d.setting) {
    layer.pocket = { shape: "channel", depthMM: Math.min(1, Math.max(0.05, state.channel.depthMM)) };
  }
  const cross = d.style === "cross-bezel" || d.style === "cross-channel" || d.style === "cross-around";
  const centreArc = wrapArc(((d.placementDeg + 90) / 360) * C, C);
  if (d.style === "eye") {
    layer.count = 1;
    layer.centerArcMM = round2(centreArc);
    return clampStonePatternLayer(layer);
  }
  if (cross) {
    // Straight across the finger: a row tilted a quarter turn, as many as the
    // usable width holds when the run is on Auto.
    layer.angleDeg = 90;
    layer.spacing = "gap";
    const usable = state.widthMM - 2 * EDGE_MARGIN_MM;
    const gap = layer.gapMM ?? 0.2;
    layer.count = d.count > 0 ? d.count : Math.max(1, Math.floor((usable + gap) / (dia + gap)));
    layer.centerArcMM = round2(centreArc);
    return clampStonePatternLayer(layer);
  }
  const fraction = d.spacing ? RING_FRACTION[d.spacing] : undefined;
  if (fraction) {
    layer.spanTurns = round3(fraction);
    layer.spacing = "fit";
    if (d.count > 0) layer.count = d.count;
  } else if (d.spreadDeg >= 360) {
    layer.spanTurns = 1;
    layer.spacing = "fit";
    if (d.count > 0) layer.count = d.count;
  } else if (d.count > 0) {
    layer.count = d.count;
    layer.spacing = "gap";
  } else {
    layer.spanTurns = round3(Math.max(0.01, d.spreadDeg / 360));
    layer.spacing = "gap";
  }
  if (!(layer.spanTurns === 1)) layer.centerArcMM = round2(centreArc);
  const y = d.rowPosition * rowOffsetMM(halfW, dia);
  if (Math.abs(y) > 1e-6) layer.centerYMM = round3(y);
  return clampStonePatternLayer(layer);
}

/** Start from current row: layer 1 replaced by the run's law, the rest kept. */
export function startFromRow(state: RingState): StonePattern {
  const p = currentPattern(state);
  return replaceLayer({ layers: p.layers }, 0, layerFromRun(state));
}

// ─── Convert to free stones ──────────────────────────────────────────────────

/**
 * The pattern's stones as a free arrangement, in the free editor's own
 * vocabulary (`gemPlacement.freeRun`): `thetaDeg` in the stone frame (−90° is
 * 12 o'clock, the pattern plane's arc 0), `u` normalised by the seat's bound
 * (`rowLimitMM`), the size as a MULTIPLE of the run's stone (so the Stone Size
 * slider still drives the set), and each layer's setting per stone. Recesses
 * and lines beside the stones are pattern features and do not survive.
 */
export function patternToFreeStones(pattern: StonePattern, state: RingState): FreeStone[] {
  const band = patternBand(state);
  const res = expandStonePattern(pattern, band);
  const R = bandRadiusMM(band);
  const halfW = state.widthMM / 2;
  const run = state.diamond.stoneSizeMM > 0 ? state.diamond.stoneSizeMM : 1.5;
  const settingOf = pattern.layers.map((l) => STONE_PATTERN_SETTINGS[resolveStonePatternLayer(l).setting].setting);
  const stones = res.stones.map((s, i): Partial<FreeStone> => {
    const limit = Math.max(0, halfW - SEAT_HALF_FACTOR * s.d);
    const out: Partial<FreeStone> = {
      id: `pt${i + 1}`,
      thetaDeg: round3((s.arcMM / R) * (180 / Math.PI) - 90),
      u: limit > 0 ? round4(Math.max(-1, Math.min(1, s.yMM / limit))) : 0,
    };
    const scale = s.d / run;
    if (Math.abs(scale - 1) > 1e-4) out.sizeScale = round4(scale);
    const setting: DiamondSetting | undefined = settingOf[s.layer];
    if (setting) out.setting = setting;
    return out;
  });
  return clampFreeStones(stones);
}

/** The patch Convert sends: the free style with the stones, laid out once
 *  (no repeat, no rotation — the pattern already placed every copy). */
export function convertToFreePatch(pattern: StonePattern, state: RingState): RingDesignPatch {
  return { diamond: { style: "free-place" }, freeStones: patternToFreeStones(pattern, state), freeRepeat: 1, freeRotateDeg: 0 };
}

// ─── library and Surprise me ─────────────────────────────────────────────────

/** The patch picking a library entry sends: its layers, as a fresh copy. */
export function libraryPatch(id: string): RingDesignPatch | null {
  const p = stonePatternFromLibrary(id);
  return p ? patternPatch(p) : null;
}

/** A fresh seed for Surprise me: any 31-bit integer. The generator is
 *  deterministic per seed, so the randomness lives here, in the click. */
export function freshSeed(rand: () => number = Math.random): number {
  return Math.floor(rand() * 2147483647);
}

export function surprisePatch(seed: number, state: RingState): RingDesignPatch {
  return patternPatch(surpriseStonePattern(seed, patternBand(state)));
}

/** Library search: every word must appear in the name, category or id. */
export function filterLibrary(
  entries: readonly StonePatternLibraryEntry[],
  category: string | null,
  query: string,
): StonePatternLibraryEntry[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  return entries.filter((e) => {
    if (category && e.category !== category) return false;
    if (!words.length) return true;
    const hay = `${e.name} ${e.category} ${e.id}`.toLowerCase();
    return words.every((w) => hay.includes(w));
  });
}

/** The band a library thumbnail is drawn on: the entry's own design width. */
export function libraryBand(entry: Pick<StonePatternLibraryEntry, "widthMM">): StonePatternBand {
  return { ...STONE_PATTERN_LIBRARY_BAND, widthMM: entry.widthMM };
}

export const LIBRARY_SIZE = STONE_PATTERN_LIBRARY.length;

// ─── thumbnails ──────────────────────────────────────────────────────────────

export interface ThumbStone { cx: number; cy: number; r: number; layer: number; index: number }
export interface ThumbGeometry {
  /** The unrolled band window: x is arc mm from `x0`, y is mm DOWN from the +Y edge. */
  x0: number;
  length: number;
  widthMM: number;
  circumferenceMM: number;
  /** How many strips the window is folded into (a long eternity reads better
   *  as two or three rows than as a hairline). */
  rows: number;
  stones: ThumbStone[];
  /** Closed outlines as SVG path data, in the same frame. */
  pockets: string[];
  rails: { d: string; widthMM: number }[];
  result: StonePatternResult;
}

/**
 * Lay a pattern's expansion out flat for an SVG: the window starts just after
 * the widest empty stretch of the ring, so a pattern at 6 o'clock is not cut
 * in two, and every stone keeps exactly the generator's arc (shifted by whole
 * circumferences only), y and diameter.
 */
export function thumbGeometry(pattern: StonePattern | null | undefined, band: StonePatternBand, opts: { minLengthMM?: number; foldAtMM?: number; maxRows?: number } = {}): ThumbGeometry {
  const C = band.circumferenceMM ?? 65;
  const W = band.widthMM;
  const halfW = W / 2;
  const result = expandStonePattern(pattern ?? null, band);
  const stones = result.stones;
  const pad = 0.6;
  let start = -C / 2;
  let extent = 0;
  if (stones.length) {
    const lo = stones.map((s) => s.arcMM - s.d / 2);
    const sorted = stones.map((s, i) => ({ a: s.arcMM, i })).sort((p, q) => p.a - q.a);
    // The widest circular gap between neighbouring stone centres: cut there.
    let bestGap = -1;
    let cut = 0;
    for (let k = 0; k < sorted.length; k++) {
      const a = sorted[k].a;
      const b = k + 1 < sorted.length ? sorted[k + 1].a : sorted[0].a + C;
      if (b - a > bestGap) { bestGap = b - a; cut = (k + 1) % sorted.length; }
    }
    const firstArc = sorted[cut].a;
    const unwrap = (a: number) => firstArc + mod(a - firstArc, C);
    let minX = Infinity;
    let maxX = -Infinity;
    stones.forEach((s, i) => {
      const u = unwrap(s.arcMM);
      minX = Math.min(minX, lo[i] - s.arcMM + u);
      maxX = Math.max(maxX, u + s.d / 2);
    });
    // A full eternity's gap closes on itself: show exactly one turn.
    if (maxX - minX > C - pad) { minX = firstArc - (C - (maxX - minX)) / 2; maxX = minX + C; }
    start = minX - pad;
    extent = maxX - minX + 2 * pad;
  }
  const minLength = opts.minLengthMM ?? Math.max(2.5 * W, 8);
  if (extent < minLength) { start -= (minLength - extent) / 2; extent = minLength; }
  if (extent > C) extent = C;
  const foldAt = opts.foldAtMM ?? 7 * W;
  const rows = Math.max(1, Math.min(opts.maxRows ?? 3, Math.ceil(extent / foldAt - 0.15)));
  const place = (a: number) => start + mod(a - start, C);
  const yOf = (y: number) => halfW - y;

  const thumbStones = stones.map((s: PatternStone): ThumbStone => ({
    cx: place(s.arcMM) - start, cy: yOf(s.yMM), r: s.d / 2, layer: s.layer, index: s.index,
  }));
  const poly = (pts: { arcMM: number; yMM: number }[], close: boolean) => {
    if (!pts.length) return "";
    const shift = place(pts[0].arcMM) - pts[0].arcMM;
    return pts.map((p, k) => `${k ? "L" : "M"}${fmt(p.arcMM + shift - start)} ${fmt(yOf(p.yMM))}`).join(" ") + (close ? " Z" : "");
  };
  const pockets = result.pockets.map((q) => {
    if (q.ring) return `M0 ${fmt(yOf(q.ring.yMaxMM))} H${fmt(extent)} V${fmt(yOf(q.ring.yMinMM))} H0 Z`;
    return poly(q.outline, true);
  }).filter(Boolean);
  const rails = result.rails.map((rl: PatternRail) => ({
    widthMM: rl.widthMM,
    d: rl.kind === "horizontal" || !rl.points?.length
      ? `M0 ${fmt(yOf(rl.yMM ?? 0))} H${fmt(extent)}`
      : poly(rl.points, false),
  }));
  return { x0: start, length: extent, widthMM: W, circumferenceMM: C, rows, stones: thumbStones, pockets, rails, result };
}

// ─── the layer card, as data ─────────────────────────────────────────────────

/** What a control needs to know beyond the layer: the band it sits on. */
export interface LayerCtx {
  band: Required<Omit<StonePatternBand, "edgeMarginMM">>;
}

type Read<T> = (L: ResolvedStonePatternLayer, raw: StonePatternLayer, c: LayerCtx) => T;
type Write<T> = (raw: StonePatternLayer, v: T, c: LayerCtx) => StonePatternLayer;
type Show = (L: ResolvedStonePatternLayer, raw: StonePatternLayer) => boolean;

export type LayerSection = "Shape" | "Length" | "Stones" | "Spacing" | "Setting" | "Recess" | "Lines" | "Copies";
export const LAYER_SECTIONS: LayerSection[] = ["Shape", "Length", "Stones", "Spacing", "Setting", "Recess", "Lines", "Copies"];

export interface ChipCtl {
  kind: "chips";
  key: string;
  section: LayerSection;
  label: string;
  options: { id: string; label: string }[];
  read: Read<string>;
  write: Write<string>;
  show?: Show;
  /** A hint under the row, in plain words. */
  hint?: string;
}
export interface SliderCtl {
  kind: "slider";
  key: string;
  section: LayerSection;
  label: string;
  min: (c: LayerCtx) => number;
  max: (c: LayerCtx) => number;
  step: number;
  unit?: string;
  display?: (v: number, c: LayerCtx, L: ResolvedStonePatternLayer) => string;
  read: Read<number>;
  write: Write<number>;
  show?: Show;
  hint?: string;
}
export interface ToggleCtl {
  kind: "toggle";
  key: string;
  section: LayerSection;
  label: string;
  read: Read<boolean>;
  write: Write<boolean>;
  show?: Show;
  hint?: string;
}
export type LayerCtl = ChipCtl | SliderCtl | ToggleCtl;

const CURVES = new Set<StonePatternPath>(["sweep", "chevron", "zigzag", "arc", "wave", "s-curve"]);
const TILTABLE = new Set<StonePatternPath>(["row", "diagonal", "sweep", "chevron", "zigzag", "arc", "wave", "s-curve", "multi-row", "custom"]);

export const PATH_OPTIONS: { id: StonePatternPath; label: string }[] = [
  { id: "row", label: "Line" },
  { id: "diagonal", label: "Diagonal" },
  { id: "sweep", label: "Sweep" },
  { id: "chevron", label: "Chevron" },
  { id: "zigzag", label: "Zigzag" },
  { id: "arc", label: "Smile" },
  { id: "wave", label: "Wave" },
  { id: "s-curve", label: "S-curve" },
  { id: "multi-row", label: "Rows" },
  { id: "cluster", label: "Cluster" },
  { id: "rosette", label: "Flower" },
  { id: "scatter", label: "Scatter" },
];

const LENGTHS = [
  { id: "stones", label: "Just the stones", turns: null },
  { id: "quarter", label: "Quarter", turns: 0.25 },
  { id: "half", label: "Half", turns: 0.5 },
  { id: "three", label: "Three-quarter", turns: 0.75 },
  { id: "full", label: "Full", turns: 1 },
] as const;

const GROUP_PRESETS: { id: string; label: string; groups: number[] | null }[] = [
  { id: "none", label: "One run", groups: null },
  { id: "3-1-3", label: "3 · 1 · 3", groups: [3, 1, 3] },
  { id: "1-3-1", label: "1 · 3 · 1", groups: [1, 3, 1] },
  { id: "2-2", label: "2 · 2", groups: [2, 2] },
  { id: "3-3", label: "3 · 3", groups: [3, 3] },
  { id: "2-2-2", label: "2 · 2 · 2", groups: [2, 2, 2] },
  { id: "5-1-5", label: "5 · 1 · 5", groups: [5, 1, 5] },
];
const groupsId = (g: number[] | null) => (g && g.length ? g.join("-") : "none");

const SIZING_OPTIONS: { id: StonePatternSizingMode; label: string }[] = [
  { id: "uniform", label: "Even" },
  { id: "graduated", label: "Graduated" },
  { id: "alternating", label: "Alternating" },
  { id: "random", label: "Mixed" },
];

export const SETTING_OPTIONS: { id: StonePatternSetting; label: string }[] =
  (Object.keys(STONE_PATTERN_SETTINGS) as StonePatternSetting[]).map((id) => ({ id, label: STONE_PATTERN_SETTINGS[id].label }));

const POCKET_OPTIONS: { id: StonePatternPocketShape; label: string }[] = [
  { id: "none", label: "None" },
  { id: "capsule", label: "Trough" },
  { id: "channel", label: "Channel" },
  { id: "navette", label: "Eye" },
  { id: "plate", label: "Plate" },
];

const MIRROR_OPTIONS: { id: StonePatternMirror; label: string }[] = [
  { id: "none", label: "None" },
  { id: "across", label: "Flip across" },
  { id: "along", label: "Flip along" },
  { id: "point", label: "Turn half" },
  { id: "both", label: "All four" },
];

const hasPocket: Show = (L) => L.pocket.shape !== "none";
const hasRails: Show = (L) => L.rails != null;
/** Set a nested block's field, keeping the rest of the block as it was. */
const nested = <K extends "sizing" | "pocket" | "rails">(raw: StonePatternLayer, block: K, patch: Partial<NonNullable<StonePatternLayer[K]>>): StonePatternLayer =>
  ({ ...raw, [block]: { ...(raw[block] ?? {}), ...patch } });
const pct = (v: number) => `${Math.round(v * 100)}%`;
const mm = (v: number) => `${v.toFixed(2)} mm`;

/** Degrees round the ring, 0 at the top, + toward 3 o'clock. */
export const arcToDeg = (arcMM: number, C: number) => round1((arcMM / C) * 360);
export const degToArc = (deg: number, C: number) => round3((deg / 360) * C);

/**
 * Every layer control, in display order. The card renders this table and
 * nothing else, so a control that is here is on screen (when `show` says so)
 * and writes exactly what `write` returns.
 */
export const LAYER_CONTROLS: LayerCtl[] = [
  // ── Shape ──
  {
    kind: "chips", key: "path", section: "Shape", label: "Shape", options: PATH_OPTIONS,
    read: (L) => L.path,
    write: (raw, v) => {
      const next: StonePatternLayer = { ...raw, path: v as StonePatternPath };
      // A curve's swing belongs to the curve it was set for.
      delete next.amplitudeMM;
      if (v !== "diagonal" && raw.path === "diagonal") delete next.angleDeg;
      return next;
    },
  },
  {
    kind: "slider", key: "centerArc", section: "Shape", label: "Round the ring", min: () => -180, max: () => 180, step: 1,
    display: (v) => (Math.abs(v) < 0.5 ? "Top" : Math.abs(Math.abs(v) - 180) < 0.5 ? "Bottom" : `${v > 0 ? "+" : ""}${Math.round(v)}°`),
    read: (L, _raw, c) => arcToDeg(L.centerArcMM, c.band.circumferenceMM),
    write: (raw, v, c) => ({ ...raw, centerArcMM: degToArc(v, c.band.circumferenceMM) }),
    hint: "Where the layer sits round the finger: Top is 12 o'clock, + turns toward 3 o'clock.",
  },
  {
    kind: "slider", key: "centerY", section: "Shape", label: "Across the band",
    min: (c) => -round2(c.band.widthMM / 2), max: (c) => round2(c.band.widthMM / 2), step: 0.05, unit: "mm",
    display: (v) => (Math.abs(v) < 0.025 ? "Centre" : `${v > 0 ? "+" : ""}${v.toFixed(2)} mm`),
    read: (L) => L.centerYMM, write: (raw, v) => ({ ...raw, centerYMM: v }),
    // A scatter spreads over the whole width of the band; it has no centre line.
    show: (L) => L.path !== "scatter",
  },
  {
    kind: "slider", key: "angle", section: "Shape", label: "Tilt", min: () => -90, max: () => 90, step: 1, unit: "°",
    read: (L) => L.angleDeg, write: (raw, v) => ({ ...raw, angleDeg: v }),
    show: (L) => TILTABLE.has(L.path),
  },
  {
    kind: "slider", key: "amplitude", section: "Shape", label: "Swing",
    min: (c) => -round1(Math.max(1, c.band.widthMM)), max: (c) => round1(Math.max(1, c.band.widthMM)), step: 0.05, unit: "mm",
    display: (v, c) => (Math.abs(v) >= c.band.widthMM / 2 - 0.05 ? "Band edge to edge" : mm(v)),
    read: (L, _raw, c) => Math.max(-Math.max(1, c.band.widthMM), Math.min(Math.max(1, c.band.widthMM), L.amplitudeMM)),
    write: (raw, v) => ({ ...raw, amplitudeMM: v }),
    show: (L) => CURVES.has(L.path),
    hint: "How far the curve travels across the band. It never leaves the metal.",
  },
  {
    kind: "slider", key: "waves", section: "Shape", label: "Waves", min: () => 0.5, max: () => 12, step: 0.5,
    read: (L) => L.waves, write: (raw, v) => ({ ...raw, waves: v }),
    show: (L) => L.path === "wave" || L.path === "zigzag",
  },
  {
    kind: "slider", key: "rows", section: "Shape", label: "Rows", min: () => 1, max: () => 3, step: 1,
    read: (L) => L.rows, write: (raw, v) => ({ ...raw, rows: Math.round(v) }),
    show: (L) => L.path === "multi-row",
  },
  {
    kind: "slider", key: "rowGap", section: "Shape", label: "Gap between rows", min: () => STONE_PATTERN_MIN_GAP_MM, max: () => 1.5, step: 0.01, unit: "mm",
    read: (L) => L.rowGapMM, write: (raw, v) => ({ ...raw, rowGapMM: v }),
    show: (L) => L.path === "multi-row" && L.rows > 1,
  },
  {
    kind: "toggle", key: "stagger", section: "Shape", label: "Brick offset",
    read: (L) => L.stagger, write: (raw, v) => ({ ...raw, stagger: v }),
    show: (L) => L.path === "multi-row" && L.rows > 1,
    hint: "Every other row slides half a stone along, so the rows nest.",
  },
  {
    kind: "slider", key: "seed", section: "Shape", label: "Variation", min: () => 1, max: () => 999, step: 1,
    read: (L) => Math.max(1, Math.min(999, L.seed)), write: (raw, v) => ({ ...raw, seed: Math.round(v) }),
    show: (L) => L.path === "scatter" || L.sizing.mode === "random",
    hint: "Each number is a different, repeatable arrangement.",
  },

  // ── Length & count ──
  {
    kind: "chips", key: "length", section: "Length", label: "Length",
    options: [...LENGTHS.map((l) => ({ id: l.id, label: l.label })), { id: "custom", label: "Custom" }],
    read: (L) => {
      if (L.spanTurns != null) return LENGTHS.find((l) => l.turns != null && Math.abs(l.turns - (L.spanTurns ?? 0)) < 1e-3)?.id ?? "custom";
      return L.spanMM != null ? "custom" : "stones";
    },
    write: (raw, v, c) => {
      const next = { ...raw };
      delete next.spanTurns;
      delete next.spanMM;
      const l = LENGTHS.find((x) => x.id === v);
      if (l && l.turns != null) next.spanTurns = l.turns;
      else if (v === "custom") next.spanMM = round1(Math.min(c.band.circumferenceMM / 2, 12));
      return next;
    },
    hint: "A share of the ring spreads the stones over it; Just the stones takes only the room they need.",
  },
  {
    kind: "slider", key: "spanMM", section: "Length", label: "Length (custom)",
    min: () => 0, max: (c) => Math.round(c.band.circumferenceMM), step: 0.5, unit: "mm",
    read: (L, _raw, c) => L.spanMM ?? (L.spanTurns != null ? round1(L.spanTurns * c.band.circumferenceMM) : 0),
    write: (raw, v) => { const next = { ...raw, spanMM: v }; delete next.spanTurns; return next; },
    show: (L) => L.spanMM != null && L.spanTurns == null,
  },
  {
    kind: "slider", key: "count", section: "Length", label: "Number of stones", min: () => 0, max: () => 120, step: 1,
    display: (v, _c, L) => (v === 0 ? (L.spanMM != null || L.spanTurns != null ? "As many as fit" : "7") : String(Math.round(v))),
    read: (L) => L.count, write: (raw, v) => ({ ...raw, count: Math.round(v) }),
    show: (L) => L.groups == null,
  },
  {
    kind: "chips", key: "groups", section: "Length", label: "Groups",
    options: GROUP_PRESETS.map((g) => ({ id: g.id, label: g.label })),
    read: (L) => groupsId(L.groups),
    write: (raw, v) => {
      const next = { ...raw };
      const g = GROUP_PRESETS.find((x) => x.id === v)?.groups ?? (v === "none" ? null : v.split("-").map(Number).filter((n) => n > 0));
      if (g && g.length) next.groups = g;
      else delete next.groups;
      return next;
    },
    hint: "Stones in clusters with a wider gap between them — three, one, three reads as a station band.",
  },
  {
    kind: "slider", key: "groupGap", section: "Length", label: "Gap between groups", min: () => 0, max: () => 6, step: 0.05, unit: "mm",
    read: (L) => L.groupGapMM, write: (raw, v) => ({ ...raw, groupGapMM: v }),
    show: (L) => L.groups != null,
  },
  {
    kind: "slider", key: "offset", section: "Length", label: "Slide along", min: () => -10, max: () => 10, step: 0.05, unit: "mm",
    read: (L) => Math.max(-10, Math.min(10, L.offsetMM)), write: (raw, v) => ({ ...raw, offsetMM: v }),
    hint: "Moves the stones along their line; the line itself stays put.",
  },

  // ── Stones ──
  {
    kind: "chips", key: "sizeFrom", section: "Stones", label: "Stone size",
    options: [{ id: "row", label: "Follow Stone Size" }, { id: "own", label: "Own size" }],
    read: (L) => (L.sizeMM == null ? "row" : "own"),
    write: (raw, v, c) => {
      const next = { ...raw };
      if (v === "own") next.sizeMM = round2(raw.sizeMM ?? c.band.stoneSizeMM);
      else delete next.sizeMM;
      return next;
    },
  },
  {
    kind: "slider", key: "size", section: "Stones", label: "Size", min: () => 0.5, max: () => 4, step: 0.05, unit: "mm",
    read: (L, _raw, c) => L.sizeMM ?? c.band.stoneSizeMM, write: (raw, v) => ({ ...raw, sizeMM: v }),
    show: (L) => L.sizeMM != null,
  },
  {
    kind: "chips", key: "sizing", section: "Stones", label: "Sizes along the layer", options: SIZING_OPTIONS,
    read: (L) => L.sizing.mode, write: (raw, v) => nested(raw, "sizing", { mode: v as StonePatternSizingMode }),
  },
  {
    kind: "slider", key: "endScale", section: "Stones", label: "Smallest stone", min: () => 0.2, max: () => 1, step: 0.05, display: pct,
    read: (L) => Math.min(1, L.sizing.endScale), write: (raw, v) => nested(raw, "sizing", { endScale: v }),
    show: (L) => L.sizing.mode === "graduated",
  },
  {
    kind: "slider", key: "curve", section: "Stones", label: "Fall-off", min: () => 0.25, max: () => 4, step: 0.05,
    display: (v) => (v < 0.95 ? "Quick" : v > 1.05 ? "Slow" : "Even"),
    read: (L) => L.sizing.curve, write: (raw, v) => nested(raw, "sizing", { curve: v }),
    show: (L) => L.sizing.mode === "graduated",
  },
  {
    kind: "chips", key: "anchor", section: "Stones", label: "Biggest stone",
    options: [{ id: "centred", label: "In the middle" }, { id: "one-sided", label: "At one end" }],
    read: (L) => L.sizing.anchor, write: (raw, v) => nested(raw, "sizing", { anchor: v as "centred" | "one-sided" }),
    show: (L) => L.sizing.mode === "graduated",
  },
  {
    kind: "slider", key: "altScale", section: "Stones", label: "Second size", min: () => 0.2, max: () => 2, step: 0.05, display: pct,
    read: (L) => Math.min(2, L.sizing.altScale), write: (raw, v) => nested(raw, "sizing", { altScale: v }),
    show: (L) => L.sizing.mode === "alternating",
  },
  {
    kind: "slider", key: "period", section: "Stones", label: "Stones per run", min: () => 1, max: () => 4, step: 1,
    read: (L) => L.sizing.period, write: (raw, v) => nested(raw, "sizing", { period: Math.round(v) }),
    show: (L) => L.sizing.mode === "alternating",
  },
  {
    kind: "slider", key: "randMin", section: "Stones", label: "Smallest", min: () => 0.2, max: () => 2, step: 0.05, display: pct,
    read: (L) => Math.min(2, L.sizing.min), write: (raw, v) => nested(raw, "sizing", { min: v, max: Math.max(v, raw.sizing?.max ?? 1.2) }),
    show: (L) => L.sizing.mode === "random",
  },
  {
    kind: "slider", key: "randMax", section: "Stones", label: "Largest", min: () => 0.2, max: () => 2, step: 0.05, display: pct,
    read: (L) => Math.min(2, L.sizing.max), write: (raw, v) => nested(raw, "sizing", { max: v, min: Math.min(v, raw.sizing?.min ?? 0.6) }),
    show: (L) => L.sizing.mode === "random",
  },

  // ── Spacing ──
  {
    kind: "chips", key: "spacing", section: "Spacing", label: "Spacing",
    options: [{ id: "gap", label: "Set gap" }, { id: "fit", label: "Spread evenly" }],
    read: (L) => L.spacing, write: (raw, v) => ({ ...raw, spacing: v as "gap" | "fit" }),
    // Without a length there is nothing to spread over: both read the same.
    show: (L) => L.spanMM != null || L.spanTurns != null,
    hint: "Set gap keeps the gap you choose; Spread evenly fills the length, never closer than that gap.",
  },
  {
    kind: "slider", key: "gap", section: "Spacing", label: "Gap between stones", min: () => STONE_PATTERN_MIN_GAP_MM, max: () => 2, step: 0.01, unit: "mm",
    display: (v) => (v <= STONE_PATTERN_MIN_GAP_MM + 0.005 ? "Nearly touching" : mm(v)),
    read: (L) => Math.min(2, L.gapMM), write: (raw, v) => ({ ...raw, gapMM: v }),
    // Under Spread evenly with a FIXED count the stones fill the length
    // whatever the gap; the gap only counts them when the count is automatic.
    show: (L) => L.spacing === "gap" || (L.groups == null && L.count === 0),
  },

  // ── Setting ──
  {
    kind: "chips", key: "setting", section: "Setting", label: "How the stones are held", options: SETTING_OPTIONS,
    read: (L) => L.setting, write: (raw, v) => ({ ...raw, setting: v as StonePatternSetting }),
  },

  // ── Recess ──
  {
    kind: "chips", key: "pocket", section: "Recess", label: "Recess", options: POCKET_OPTIONS,
    read: (L) => L.pocket.shape, write: (raw, v) => nested(raw, "pocket", { shape: v as StonePatternPocketShape }),
    hint: "A lowered floor the stones sit in. Trough and Channel follow the stones; Eye and Plate are cut as one shape.",
  },
  {
    kind: "slider", key: "pocketDepth", section: "Recess", label: "Depth", min: () => 0.05, max: () => 1, step: 0.01, unit: "mm",
    read: (L) => L.pocket.depthMM, write: (raw, v) => nested(raw, "pocket", { depthMM: v }), show: hasPocket,
  },
  {
    kind: "slider", key: "pocketClear", section: "Recess", label: "Metal round the stones", min: () => 0.05, max: () => 1, step: 0.01, unit: "mm",
    read: (L) => L.pocket.clearanceMM, write: (raw, v) => nested(raw, "pocket", { clearanceMM: v }), show: hasPocket,
  },
  {
    kind: "slider", key: "pocketCorner", section: "Recess", label: "Corner rounding", min: () => 0.05, max: () => 3, step: 0.05, unit: "mm",
    read: (L) => L.pocket.cornerRadiusMM, write: (raw, v) => nested(raw, "pocket", { cornerRadiusMM: v }),
    show: (L) => L.pocket.shape === "navette" || L.pocket.shape === "plate",
  },
  {
    kind: "chips", key: "pocketPer", section: "Recess", label: "One recess for",
    options: [{ id: "group", label: "Each group" }, { id: "layer", label: "The whole layer" }],
    read: (L) => L.pocket.per, write: (raw, v) => nested(raw, "pocket", { per: v as "group" | "layer" }), show: hasPocket,
  },
  {
    kind: "slider", key: "pocketMin", section: "Recess", label: "Only groups of at least", min: () => 1, max: () => 10, step: 1,
    display: (v) => `${Math.round(v)} stone${Math.round(v) === 1 ? "" : "s"}`,
    read: (L) => Math.min(10, L.pocket.minGroup), write: (raw, v) => nested(raw, "pocket", { minGroup: Math.round(v) }),
    // Only groups can be smaller than each other; one run is one group.
    show: (L) => L.pocket.shape !== "none" && L.pocket.per === "group" && L.groups != null,
  },

  // ── Lines beside the stones ──
  {
    kind: "toggle", key: "rails", section: "Lines", label: "Lines beside the stones",
    read: (L) => L.rails != null,
    write: (raw, v) => { const next = { ...raw }; if (v) next.rails = raw.rails ?? {}; else delete next.rails; return next; },
    hint: "Thin engraved lines that run beside the stones, or all the way round.",
  },
  {
    kind: "chips", key: "railSides", section: "Lines", label: "Sides",
    options: [{ id: "left", label: "Left" }, { id: "right", label: "Right" }, { id: "both", label: "Both" }],
    read: (L) => L.rails?.sides ?? "both", write: (raw, v) => nested(raw, "rails", { sides: v as "left" | "right" | "both" }), show: hasRails,
  },
  {
    kind: "chips", key: "railExtent", section: "Lines", label: "Length",
    options: [{ id: "stones", label: "Beside the stones" }, { id: "all-round", label: "All the way round" }],
    read: (L) => L.rails?.extent ?? "stones", write: (raw, v) => nested(raw, "rails", { extent: v as "stones" | "all-round" }), show: hasRails,
  },
  {
    kind: "slider", key: "railGap", section: "Lines", label: "Gap to the stones", min: () => 0, max: () => 1.5, step: 0.01, unit: "mm",
    read: (L) => Math.min(1.5, L.rails?.gapMM ?? 0.2), write: (raw, v) => nested(raw, "rails", { gapMM: v }), show: hasRails,
  },
  {
    kind: "slider", key: "railWidth", section: "Lines", label: "Line width", min: () => 0.15, max: () => 1.5, step: 0.01, unit: "mm",
    read: (L) => L.rails?.widthMM ?? 0.3, write: (raw, v) => nested(raw, "rails", { widthMM: v }), show: hasRails,
  },
  {
    kind: "slider", key: "railDepth", section: "Lines", label: "Line depth", min: () => 0.1, max: () => 0.6, step: 0.01, unit: "mm",
    read: (L) => L.rails?.depthMM ?? 0.2, write: (raw, v) => nested(raw, "rails", { depthMM: v }), show: hasRails,
  },

  // ── Copies ──
  {
    kind: "slider", key: "repeat", section: "Copies", label: "Copies round the ring", min: () => 1, max: () => 24, step: 1,
    display: (v) => (Math.round(v) === 1 ? "Just one" : `${Math.round(v)} copies`),
    read: (L) => Math.min(24, L.repeat), write: (raw, v) => ({ ...raw, repeat: Math.round(v) }),
  },
  {
    kind: "chips", key: "mirror", section: "Copies", label: "Mirror", options: MIRROR_OPTIONS,
    read: (L) => L.mirror, write: (raw, v) => ({ ...raw, mirror: v as StonePatternMirror }),
    hint: "Flip across copies the layer to the other side of the centre line; Flip along to the other side of 12 o'clock.",
  },
];

export const controlByKey = (key: string) => LAYER_CONTROLS.find((c) => c.key === key);

/** The controls on screen for this layer, in order. */
export function visibleControls(raw: StonePatternLayer): LayerCtl[] {
  const L = resolveStonePatternLayer(raw);
  return LAYER_CONTROLS.filter((c) => !c.show || c.show(L, raw));
}

/** A one-line summary for a folded layer card. */
export function layerSummary(raw: StonePatternLayer, c: LayerCtx): string {
  const L = resolveStonePatternLayer(raw);
  const shape = PATH_OPTIONS.find((p) => p.id === L.path)?.label ?? "Custom";
  const n = expandStonePattern({ layers: [raw] }, c.band).stones.length;
  return `${shape} · ${n} stone${n === 1 ? "" : "s"} · ${STONE_PATTERN_SETTINGS[L.setting].label}`;
}

/**
 * The handler every control on a layer card goes through. Chips and toggles
 * are discrete edits — `apply(...)` stores the RingState the call returns.
 * Sliders send without `apply`, exactly as the neighbouring Gems rows do: the
 * kit Slider wraps the drag in the draft controller (beginDraft / endDraft),
 * and the viewer streams `ringStateChanged` while it moves.
 */
export function layerControlHandler(
  ctx: { r: Pick<RingBuilderClientApi, "applyRingDesign">; apply: (p: Promise<RingState | null>) => Promise<void> },
  pattern: StonePattern,
  index: number,
  lc: LayerCtx,
) {
  return (ctl: LayerCtl, value: string | number | boolean): Promise<unknown> => {
    const raw = pattern.layers[index] ?? {};
    const next = replaceLayer(pattern, index, (ctl.write as Write<typeof value>)(raw, value, lc));
    const call = sendPattern(ctx.r, next);
    return ctl.kind === "slider" ? call : ctx.apply(call);
  };
}

/** The junction-hardware size slider (beads / claws) is read for a pattern
 *  layer set Pavé or Shared claws too, not only for the run's setting. */
export function patternJunctionSetting(state: RingState): "pave" | "shared-prong" | null {
  if (state.diamond.style !== PATTERN_STYLE) return null;
  for (const l of currentPattern(state).layers) {
    const s = STONE_PATTERN_SETTINGS[resolveStonePatternLayer(l).setting].setting;
    if (s === "pave" || s === "shared-prong") return s;
  }
  return null;
}

// ─── small numbers ───────────────────────────────────────────────────────────

function mod(a: number, n: number) { return ((a % n) + n) % n; }
function wrapArc(a: number, C: number) { return a - C * Math.floor(a / C + 0.5); }
function round1(v: number) { return Math.round(v * 10) / 10; }
function round2(v: number) { return Math.round(v * 100) / 100; }
function round3(v: number) { return Math.round(v * 1000) / 1000; }
function round4(v: number) { return Math.round(v * 10000) / 10000; }
function fmt(v: number) { return String(Math.round(v * 1000) / 1000); }
